import React, { ReactNode } from 'react'
import PreviewHeader from './PreviewHeader'
import { useCheckoutUI } from 'src/lib/contexts/CheckoutUIContext'
import { OrderSummaryConfigType } from 'src/lib/types/customizeUI'
import { ShoppingCart, X } from 'react-feather'
import Pill from '../skeletons/Pill'
import { classNames } from 'src/lib/utils/helpers'
import { useLocale } from 'src/lib/hooks/useLocal'

interface PreviewLayoutProps {
  logoUrl?: string
  hideLogo?: boolean
  showTotal?: { isEnabled?: boolean; showStrikeThrough?: boolean }
  orderSummaryConfig?: OrderSummaryConfigType
  shouldShowPillAsLogo?: boolean
  isProgressBarPreview?: boolean
  total?: number
  containerRef?: React.Ref<HTMLDivElement>
  children: ReactNode,
  bodyClassName?:string,
  mainDivClassName?:string
}

export const PreviewLayout: React.FC<PreviewLayoutProps> = ({
  logoUrl,
  hideLogo,
  showTotal,
  children,
  orderSummaryConfig,
  shouldShowPillAsLogo = false,
  containerRef,
  bodyClassName,
  mainDivClassName
}) => {
  const {
    getters: { checkoutUI }
  } = useCheckoutUI()

  return (
    <div className='flex flex-col items-center sticky top-16'>
      <div
        ref={containerRef}
        className={classNames('relative border-8 border-gray-light rounded-[40px] overflow-hidden bg-gray-lightest',mainDivClassName??"")}
      >
        <PreviewHeader
          logoUrl={logoUrl ?? checkoutUI?.logoUrl ?? 'images/image-placeholder.svg'}
          skeleton={{
            layout: 'row',
            config: [
              {
                height: 1.25,
                width: 5
              }
            ]
          }}
          isLogoHidden={hideLogo}
          showTotal={showTotal}
          orderSummaryConfig={orderSummaryConfig}
          shouldShowPillAsLogo={shouldShowPillAsLogo}
        />
        <div className={classNames("h-[600px] w-[358px] flex flex-col items-center justify-start overflow-scroll scrollbar-hide",bodyClassName??'')}>
          <div className='flex flex-col justify-between items-start w-full'>{children}</div>
        </div>
      </div>
    </div>
  )
}

export const PreviewLayoutCart: React.FC<PreviewLayoutProps> = ({ children, isProgressBarPreview = false, total }) => {
  const { t } = useLocale()
  return (
    <div className='flex flex-col items-center sticky top-16'>
      <div className='relative border-8 border-gray-light rounded-[40px] overflow-hidden bg-white'>
        <div className='flex items-center w-full p-4 border-b-1 border-gray-light bg-gray-lightest'>
          <div className='flex items-center space-x-2'>
            <ShoppingCart />
            <span className='text-md font-medium text-coal-dark'>{t('your_cart')}</span>
            <Pill height={1} width={4} />
          </div>
          <X className='cursor-pointer ml-auto' />
        </div>
        <div
          className={classNames(
            ' w-[328px] flex flex-col items-center justify-start overflow-y-scroll overflow-x-hidden custom-scrollbar',
            isProgressBarPreview ? 'h-[500px]' : 'h-[600px]'
          )}
        >
          <div className='flex flex-col items-center w-full h-full'>{children}</div>
        </div>
        <div className='mt-auto w-full'>
          {isProgressBarPreview && (
            <div className='flex justify-between items-center w-full p-4 pb-8 border-b-1 border-gray-light'>
              <div className='flex flex-col space-y-2'>
                <span className='text-md font-medium text-coal-dark'>₹{total}</span>
                <span className='text-xs underline font-medium text-coal-dark'>{t('estimated_total')}</span>
              </div>
              <Pill height={3} width={12} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export const PreviewBody: React.FC<{ children: ReactNode }> = ({ children }) => {
  return <div className='flex flex-col justify-between items-center w-full h-full p-3 space-y-3'>{children}</div>
}

export const PreviewLabel: React.FC<{ children: ReactNode }> = ({ children }) => {
  return <div className='flex flex-col justify-between items-center w-full'>{children}</div>
}

export const PreviewFooter: React.FC<{ children: ReactNode }> = ({ children }) => {
  return <div className='flex flex-col justify-between items-center w-full h-full'>{children}</div>
}
